import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";

import { useDataSource } from "@/adapters/data-source";
import { NOTIFICATIONS_KEY } from "@/components/notifications/queries";
import { router } from "@/routes/router";

/**
 * What happens when someone clicks a push notification while EstelleERP is
 * already open (E6, #446, slice 5).
 *
 * The service worker cannot move the page itself without reloading it, so it
 * posts a message: this opens the record the notification is about — an
 * ordinary in-app navigation, which passes the destination's permission check as
 * any other does — and marks the notification read, so the bell and the phone
 * agree. When the app is not open the worker opens it at that address instead
 * and nothing here runs.
 *
 * Only a path on this app is followed: the worker already refuses anything
 * else, and this checks again, because a message listener is a trust boundary
 * and one line is cheap.
 */
export function PushClickHandler() {
  const data = useDataSource();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;

    const onMessage = (event: MessageEvent) => {
      const message = event.data as {
        type?: unknown;
        path?: unknown;
        notificationId?: unknown;
      } | null;
      if (message?.type !== "open-notification") return;

      const path = message.path;
      if (
        typeof path === "string" &&
        path.startsWith("/") &&
        !path.startsWith("//")
      ) {
        router.history.push(path);
      }

      const id = message.notificationId;
      if (typeof id === "string" && id !== "") {
        void data.notifications
          .markRead(id)
          .then(() =>
            queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY }),
          )
          .catch(() => undefined);
      }
    };

    navigator.serviceWorker.addEventListener("message", onMessage);
    return () => {
      navigator.serviceWorker.removeEventListener("message", onMessage);
    };
  }, [data, queryClient]);

  return null;
}
